import { BylineDataResult } from '@/types/bylineData';
import { StandaloneComponent, StandaloneComponentProps } from '@/types/component';
import { SearchData, SearchResult } from '@/types/search';
import { getDomain } from '@/utils/getDomain';
import { getUrl } from '@/utils/getUrl';
import { http } from '@/utils/http';
import { logger } from '@/utils/logger';
import { mergeOptions } from '@/utils/merge';
import { shouldInjectAdAtRow } from 'lib/data/compute/ads/helpers';
import { Fragment, ReactNode, useCallback, useRef, useState } from 'react';
import Search from './Search';

export interface StandaloneSearchProps extends StandaloneComponentProps<typeof Search> {
  adsUniqueId?: string;
  button?: ReactNode;
  headline?: ReactNode;
  hideButton?: boolean;
  initial?: {
    results?: SearchResult[] | BylineDataResult[];
    next?: number;
  };
  page?: number;
  pageType?: typeof AUTHOR_PAGE | 'searchResults';
  query?: string;
  total?: number;
}

const AUTHOR_PAGE = 'authorPage';

export const fetchSearchResults = async (query: string, next?: number, filter?: string) => {
  const host = getDomain();
  const url = getUrl(`/api/search`, host);

  if (!url) {
    logger.error('Search: Could not construct URL');
    return {};
  }

  url?.searchParams.set('term', query);
  url?.searchParams.set('start', `${next ?? 1}`);
  url?.searchParams.set('filter', filter ?? '');

  const response = await http.get(url.href);

  return response?.data ?? {};
};

const getResults = (query: string, next: number, callback: () => void): Promise<SearchData> => {
  return fetchSearchResults(query, next).catch((error) => {
    logger.catch(error);
    return callback();
  });
};

export const StandaloneSearch: StandaloneComponent<StandaloneSearchProps> = ({
  adsUniqueId = '',
  button,
  headline,
  hideButton,
  initial,
  page,
  pageType,
  query,
  total,
  ...props
}) => {
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<SearchResult[]>(initial?.results ?? []);
  const [next, setNext] = useState(initial?.next ?? 1);
  const adPlacementNo = useRef(1);

  const handleLoadMore = useCallback(async () => {
    if (isLoading) return;

    if (!query || !next) {
      setNext(0);
      return logger.error(`Load more search results have failed.`, { query, next });
    }

    setIsLoading(true);
    const response = await getResults(query, next, () => setIsError(true));

    if (!response || response.status !== 'search_successful') {
      logger.error(response);
      return setIsError(true);
    }

    setNext(response.nextPage || 0);

    setResults([...results, ...response.items]);

    setIsLoading(false);
  }, [isLoading, next, query, results]);

  if (isError) {
    return <></>;
  }

  const isButtonDisabled = isLoading || isError;
  const showHeadline = page ? (page === 1 && pageType === AUTHOR_PAGE ? true : false) : true;

  return (
    <Search {...props}>
      <Search.Row
        button={{ content: button ?? 'Ladda mer' }}
        caption={`Hittade ${total || 0} resultat`}
        content={
          <>
            {results.map((result, index) => (
              <Fragment key={index}>
                <Search.SearchItem
                  headline={result.title}
                  caption={result.date}
                  description={result.snippet}
                  image={{ src: result.image, alt: result.title }}
                  links={{ article: { href: result.link } }}
                  options={mergeOptions({ variant: 'horizontal' as const }, props.options?.$searchItem)}
                />
                {shouldInjectAdAtRow({ currentRow: index, frequency: 3 }) && (
                  <Search.Adunit
                    data={{ placementType: 'responsive', adIndex: adPlacementNo.current++, adsUniqueId }}
                    options={props.options?.$adunit}
                  />
                )}
              </Fragment>
            ))}
            {isLoading && <Search.Spinner options={props.options?.$spinner} />}
          </>
        }
        headline={
          showHeadline &&
          (headline || (
            <h1>
              {pageType === AUTHOR_PAGE ? 'Artiklar av ' : 'Sökresultat för '}
              <em>{query || '?'}</em>
            </h1>
          ))
        }
        options={mergeOptions(
          {
            $button: {
              className: hideButton ? 'hidden' : '',
              disabled: isButtonDisabled,
              onClick: handleLoadMore,
            },
          },
          props.options?.$row,
        )}
      />
    </Search>
  );
};
